import React, { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'en' | 'zh' | 'bm';

export interface Translations {
  // Login page
  login: {
    welcomeBack: string;
    enterCredentials: string;
    username: string;
    usernamePlaceholder: string;
    password: string;
    signIn: string;
    authenticating: string;
    error: string;
    secureSSL: string;
    encryption: string;
    copyright: string;
    captchaLabel: string;
    captchaPlaceholder: string;
    captchaError: string;
    captchaRefresh: string;
    accountLocked: string;
    attemptsRemaining: string;
  };
  // Layout / sidebar
  nav: {
    matchBetting: string;
    betHistory: string;
    afterMatchResults: string;
    userSystem: string;
    matchManagement: string;
    preMatchSummary: string;
    focusPrediction2: string;
    signOut: string;
    balance: string;
    credit: string;
    status: string;
    reportsOnly: string;
  };
  // Status badges
  status: {
    active: string;
    suspended: string;
    banned: string;
  };
  // Tiers
  tier: {
    master: string;
    company: string;
    agent: string;
    player: string;
  };
  // Bet History (Reports) page
  reports: {
    pageTitle: string;
    pageSubtitle: string;
    filters: string;
    dateFrom: string;
    dateTo: string;
    match: string;
    allMatches: string;
    allDates: string;
    username: string;
    dateTime: string;
    score: string;
    odd: string;
    betPlaced: string;
    ticketRate: string;
    statusCol: string;
    totalBet: string;
    grandTotal: string;
    noBets: string;
    pending: string;
    closed: string;
    cancelled: string;
    cancelBet: string;
    cancelling: string;
    copyright: string;
    // Additional keys
    transactionId: string;
    matchDateRange: string;
    from: string;
    to: string;
    showingDates: string;
    void: string;
    reBet: string;
    receipt: string;
    confirmVoid: string;
    confirmVoidDesc: string;
    confirmVoidBtn: string;
    voiding: string;
    incorrectPassword: string;
    enterPasswordPlaceholder: string;
    shareImage: string;
    saveImage: string;
    printReceipt: string;
    textShare: string;
    closeReceipt: string;
    preparingImage: string;
    sharing: string;
    soldOutNotice: string;
    soldOutLimit: string;
    soldOut: string;
    cancel: string;
    totalBetLabel: string;
    tierAgent: string;
    tierPlayer: string;
    tierCompany: string;
  };
  // After Match Results (Wallet) page
  wallet: {
    pageTitle: string;
    pageSubtitle: string;
    secureTransactions: string;
    footerNote: string;
    copyright: string;
    // WalletPanel internal
    filters: string;
    date: string;
    match: string;
    allMatches: string;
    allDates: string;
    dateFrom: string;
    dateTo: string;
    username: string;
    dateTime: string;
    matchCol: string;
    score: string;
    odd: string;
    betPlaced: string;
    ticketRate: string;
    totalBet: string;
    winLose: string;
    balanceBeforeFight: string;
    fightPct: string;
    balance: string;
    won: string;
    lost: string;
    grandTotal: string;
    noBets: string;
    showDetails: string;
    commission: string;
    fight: string;
    total: string;
    downline: string;
    company: string;
    betPlacedCol: string;
    winLoseCol: string;
  };
  // User System page
  users: {
    pageTitle: string;
    pageSubtitle: string;
    adminControl: string;
    cascadingStatus: string;
    cascadingStatusDesc: string;
    ticketPricing: string;
    ticketPricingDesc: string;
    fightPercentage: string;
    fightPercentageDesc: string;
  };
  // Match Betting page
  matches: {
    pageTitle: string;
    selectMatch: string;
    availableMatches: string;
    noUpcoming: string;
    noUpcomingDesc: string;
    homeWin: string;
    draw: string;
    awayWin: string;
    equalScore: string;
    bettingClosed: string;
    closesIn: string;
    startsIn: string;
    bettingOptions: string;
    placeBets: string;
    placing: string;
    backToMatches: string;
    stake: string;
    win: string;
    noBettingOdds: string;
    contactAdmin: string;
    editMatch: string;
    editMatchDesc: string;
    homeTeam: string;
    awayTeam: string;
    startTime: string;
    cancel: string;
    saveChanges: string;
    enterBetAmount: string;
    closed: string;
    viewOdds: string;
    // Additional keys
    shareMatchOdds: string;
    loadingFlags: string;
    openingShare: string;
    shareImage: string;
    saveImageTitle: string;
    saveOpenInstructions: string;
    soldOut: string;
    remark: string;
    remarkPlaceholder: string;
    viewOnly: string;
    odds: string;
  };
  // Pre-Match Summary page
  preMatch: {
    pageTitle: string;
    pageSubtitle: string;
    selectMatch: string;
    noMatches: string;
    score: string;
    odds: string;
    totalAfterRate: string;
    rebate: string;
    focus: string;
    possibleWinLoss: string;
    totalAfterCommission: string;
    backToMatches: string;
    noDownline: string;
    username: string;
    ticketRate: string;
    tickets: string;
  };
  // User Management extended
  userMgmt: {
    title: string;
    subtitle: string;
    createUser: string;
    searchPlaceholder: string;
    expandAll: string;
    collapseAll: string;
    loadingUsers: string;
    errorLoading: string;
    noUsers: string;
    colUsername: string;
    colTier: string;
    colStatus: string;
    colCreditLimit: string;
    colCreditAlloc: string;
    colFightPct: string;
    colTicketPrice: string;
    colActions: string;
    availableCredit: string;
    allocated: string;
    available: string;
    createNewUser: string;
    createDesc: string;
    initialPassword: string;
    accountTier: string;
    selectTier: string;
    ticketPrice: string;
    fightPct: string;
    selectFightPct: string;
    creditLimit: string;
    createAccount: string;
    limitTable: string;
    limitTableView: string;
    changePassword: string;
    editCreditLimit: string;
    editFightPct: string;
    editTicketPrice: string;
    activateAccount: string;
    suspendAccount: string;
    banAccount: string;
    deleteUser: string;
    actionsLabel: string;
    actionsFor: string;
    newPassword: string;
    newCreditLimit: string;
    newFightPct: string;
    newTicketPrice: string;
    saveChanges: string;
    cancel: string;
    deleteConfirm: string;
    deleteWarning: string;
    deleteSubWarning: string;
    deleteUndo: string;
    limitTableTitle: string;
    backToMatches: string;
    close: string;
    viewOnly: string;
    setToAllScores: string;
    setToAllDesc: string;
    setToAllScoresDesc: string;
    apply: string;
    colScore: string;
    colOdds: string;
    colBetsPlaced: string;
    colLimit: string;
    colRemaining: string;
    score: string;
    odds: string;
    betsPlaced: string;
    limit: string;
    remaining: string;
    loading: string;
    selectMatch: string;
    viewLimitsFor: string;
    selectMatchForLimits: string;
    andDownline: string;
    noMatches: string;
    currentLimitsFor: string;
    contactUpline: string;
    setMaxBetDesc: string;
    setMaxBetDescEnd: string;
    logInTime: string;
    logOutTime: string;
    ipAddress: string;
    duration: string;
    homeWin: string;
    awayWin: string;
    draw: string;
    aos: string;
    saveLimits: string;
    saveAllLimits: string;
    saving: string;
    userLog: string;
    userLogSubtitle: string;
    filterByUsername: string;
    from: string;
    to: string;
    search: string;
    clear: string;
    colLogIn: string;
    colLogOut: string;
    colIpAddress: string;
    colDuration: string;
    noLogs: string;
    loadingLogs: string;
    active: string;
    companyFixedFight: string;
    viewReadOnly: string;
    editLimitsNote: string;
    view: string;
    adjustBalance: string;
    deleteWarningDetail: string;
    deleteIrreversible: string;
    updateFor: string;
    newBalance: string;
    enterPassword: string;
    setCreditLimitNote: string;
    passwordUpdated: string;
    creditUpdated: string;
    statusUpdated: string;
    userCreated: string;
    userDeleted: string;
    updateFailed: string;
    createFailed: string;
    deleteFailed: string;
    statusFailed: string;
    // Nickname / Commission / Fight labels
    nickname: string;
    nicknameOptional: string;
    nicknameReceipt: string;
    setNickname: string;
    setNicknamePrompt: string;
    viewFightHistory: string;
    editCommission: string;
    fightLimit: string;
    colNickname: string;
    colCommission: string;
    colFight: string;
    colFightOnOff: string;
    commission: string;
    fight: string;
    fightOnOff: string;
    commissionPct: string;
    fightPctLimitLabel: string;
  };
  // Dashboard
  dashboard: {
    loading: string;
  };
  // Common
  common: {
    loading: string;
    allRightsReserved: string;
  };
}

const en: Translations = {
  login: {
    welcomeBack: 'Welcome Back',
    enterCredentials: 'Enter your credentials to access your dashboard',
    username: 'Username',
    usernamePlaceholder: 'Enter your username',
    password: 'Password',
    signIn: 'Sign In',
    authenticating: 'Authenticating...',
    error: 'Error',
    secureSSL: 'Secure SSL',
    encryption: '256-bit Encryption',
    copyright: '© 2026 All Rights Reserved.',
    captchaLabel: 'Security Code',
    captchaPlaceholder: 'Enter the code above',
    captchaError: 'Incorrect security code. Please try again.',
    captchaRefresh: 'Refresh',
    accountLocked: 'Account Locked',
    attemptsRemaining: 'attempts remaining before lockout',
  },
  nav: {
    matchBetting: 'Matches',
    betHistory: 'Bet History',
    afterMatchResults: 'Report',
    userSystem: 'User System',
    matchManagement: 'Match Management',
    preMatchSummary: 'Focus Prediction',
    focusPrediction2: 'Focus Prediction 2',
    signOut: 'Sign Out',
    balance: 'Balance',
    credit: 'Credit',
    status: 'Status',
    reportsOnly: 'Reports Only Mode',
  },
  status: {
    active: 'ACTIVE',
    suspended: 'SUSPENDED',
    banned: 'BANNED',
  },
  tier: {
    master: 'MASTER',
    company: 'COMPANY',
    agent: 'AGENT',
    player: 'PLAYER',
  },
  reports: {
    pageTitle: 'Bet History',
    pageSubtitle: 'Review all bets placed by you and your subordinates.',
    filters: 'Filters',
    dateFrom: 'From',
    dateTo: 'To',
    match: 'Match',
    allMatches: 'All Matches',
    allDates: 'All Dates',
    username: 'Username',
    dateTime: 'Date & Time',
    score: 'Score',
    odd: 'Odd',
    betPlaced: 'Bet Placed',
    ticketRate: 'Ticket Rate',
    statusCol: 'Status',
    totalBet: 'Total Bet',
    grandTotal: 'GRAND TOTAL',
    noBets: 'No bets found for the selected filters.',
    pending: 'Accepted',
    closed: 'Closed',
    cancelled: 'Cancelled',
    cancelBet: 'Cancel Bet',
    cancelling: 'Cancelling...',
    copyright: '© 2026 ProjectViva. All calculations follow the RM1.00 base ticket pricing and tier-specific fight percentages.',
    transactionId: 'Transaction ID',
    matchDateRange: 'Match Date Range',
    from: 'From',
    to: 'To',
    showingDates: 'Showing match dates:',
    void: 'Void',
    reBet: 'Re-Bet',
    receipt: 'Receipt',
    confirmVoid: 'Confirm Void Bet',
    confirmVoidDesc: 'Enter your password to confirm cancellation of this bet.',
    confirmVoidBtn: 'Confirm Void',
    voiding: 'Voiding…',
    incorrectPassword: 'Incorrect password. Void cancelled.',
    enterPasswordPlaceholder: 'Password',
    shareImage: 'Share Image',
    saveImage: 'Save Image',
    printReceipt: 'Print Receipt',
    textShare: 'Text Only Share (WhatsApp / WeChat / Telegram)',
    closeReceipt: '✕ Close Receipt',
    preparingImage: 'Preparing image…',
    sharing: 'Sharing…',
    soldOutNotice: 'Sold Out Notice',
    soldOutLimit: 'Sold Out — Limit Reached',
    soldOut: 'sold out',
    cancel: 'Cancel',
    totalBetLabel: 'Total Bet',
    tierAgent: 'Agent',
    tierPlayer: 'Player',
    tierCompany: 'Company',
  },
  wallet: {
    pageTitle: 'Report',
    pageSubtitle: 'View win/lose totals and balances after match settlement for your hierarchy.',
    secureTransactions: 'Secure Transactions',
    footerNote: '* Cash-in and Cash-out operations are restricted to Company Level administrators. All betting balances are calculated using the formula: Win/Lose × Fight % × Ticket Price.',
    copyright: '© 2026 Football Betting Demo Platform. All rights reserved.',
    filters: 'Filters',
    date: 'Date',
    match: 'Match',
    allMatches: 'All Matches',
    allDates: 'All Dates',
    dateFrom: 'From',
    dateTo: 'To',
    username: 'Username',
    dateTime: 'Date & Time',
    matchCol: 'Match',
    score: 'Score',
    odd: 'Odd',
    betPlaced: 'Bet Placed',
    ticketRate: 'Ticket Rate',
    totalBet: 'Total Bet',
    winLose: 'Win / Lose',
    balanceBeforeFight: 'Balance Before Fight',
    fightPct: 'Fight %',
    balance: 'Balance',
    won: 'WON',
    lost: 'LOST',
    grandTotal: 'GRAND TOTAL',
    noBets: 'No settled bets found for the selected filters.',
    showDetails: 'Show Details',
    commission: 'Rebate',
    fight: 'Fight',
    total: 'Total',
    downline: 'Downline',
    company: 'Company',
    betPlacedCol: 'Bet Placed',
    winLoseCol: 'Win/Lose',
  },
  users: {
    pageTitle: 'User System',
    pageSubtitle: 'Manage your network hierarchy, configure account permissions, and monitor agent performance.',
    adminControl: 'Administrative Control',
    cascadingStatus: 'Cascading Status',
    cascadingStatusDesc: 'Suspending or banning a parent account (Company or Agent) will automatically restrict access for all downstream agents and players in that specific line.',
    ticketPricing: 'Ticket Pricing',
    ticketPricingDesc: 'Company sets agent prices (RM 0.01 - RM 1.00). Agents set subordinate prices ≥ their own price. Cascading pricing system ensures proper hierarchy.',
    fightPercentage: 'Fight % Limit',
    fightPercentageDesc: 'Fight % limit determines the maximum fight percentage assignable to each tier. Company sets the baseline, and Agents manage limits for their respective lines.',
  },
  matches: {
    pageTitle: 'Bet',
    selectMatch: 'Select a match to place your bets. Only matches with set odds are available.',
    availableMatches: 'Available Matches',
    noUpcoming: 'No Upcoming Matches',
    noUpcomingDesc: 'Check back later for new fixtures.',
    homeWin: 'HOME WIN',
    draw: 'DRAW',
    awayWin: 'AWAY WIN',
    equalScore: 'Equal Score',
    bettingClosed: 'Betting Closed',
    closesIn: 'Closes in',
    startsIn: 'Starts in',
    bettingOptions: 'betting options available',
    placeBets: 'Place Bets',
    placing: 'Placing...',
    backToMatches: 'Back to Matches',
    stake: 'Stake:',
    win: 'Win:',
    noBettingOdds: 'No betting odds available',
    contactAdmin: 'Contact admin to set odds',
    editMatch: 'Edit Match Details',
    editMatchDesc: 'Adjust team names, match time, and betting odds.',
    homeTeam: 'Home Team',
    awayTeam: 'Away Team',
    startTime: 'Start Time',
    cancel: 'Cancel',
    saveChanges: 'Save Changes',
    enterBetAmount: 'Please enter at least one bet amount',
    closed: 'Closed',
    viewOdds: 'View Odds',
    shareMatchOdds: 'Share Match Odds',
    loadingFlags: 'Loading flags & generating…',
    openingShare: 'Opening share…',
    shareImage: 'Share Image',
    saveImageTitle: 'Save Image',
    saveOpenInstructions: 'Save the image, then open WhatsApp / Telegram / WeChat to share it.',
    soldOut: 'Sold Out',
    remark: 'Remark',
    remarkPlaceholder: 'Optional (max 15 chars)',
    viewOnly: 'View only — odds are displayed for reference. Only player accounts can place bets.',
    odds: 'Odds',
  },
  preMatch: {
    pageTitle: 'Focus Prediction',
    pageSubtitle: 'View total tickets after rate for each score across your downline.',
    selectMatch: 'Select a match to view the fight setting.',

    noMatches: 'No upcoming matches available.',
    score: 'Score',
    odds: 'Odds',
    totalAfterRate: 'Total Bets Placed',
    rebate: 'Rebate',
    focus: 'Focus',
    possibleWinLoss: 'Possible Win/Loss',
    totalAfterCommission: 'Total After Commission',
    backToMatches: 'Back to Matches',
    noDownline: 'No downline bets found for this match.',
    username: 'Username',
    ticketRate: 'Ticket Rate',
    tickets: 'Tickets',
  },
  dashboard: {
    loading: 'Loading...',
  },
  common: {
    loading: 'Loading...',
    allRightsReserved: '© 2026 BETPRO PLATFORM | ALL RIGHTS RESERVED',
  },
  userMgmt: {
    title: 'User System',
    subtitle: 'Manage accounts, hierarchies, and permissions.',
    createUser: 'Create User',
    searchPlaceholder: 'Search usernames...',
    expandAll: 'Expand All',
    collapseAll: 'Collapse All',
    loadingUsers: 'Loading users...',
    errorLoading: 'Error loading users',
    noUsers: 'No users found.',
    colUsername: 'Username',
    colTier: 'Tier',
    colStatus: 'Status',
    colCreditLimit: 'Credit Limit',
    colCreditAlloc: 'Credit Allocation',
    colFightPct: 'Fight %',
    colTicketPrice: 'Ticket Price',
    colActions: 'Actions',
    availableCredit: 'Available Credit',
    allocated: 'Allocated',
    available: 'Available',
    createNewUser: 'Create New User Account',
    createDesc: 'Add a new user to the system.',
    initialPassword: 'Initial Password',
    accountTier: 'Account Tier',
    selectTier: 'Select tier',
    ticketPrice: 'Ticket Price (RM)',
    fightPct: 'Fight %',
    selectFightPct: 'Select fight %',
    creditLimit: 'Credit Limit (RM)',
    createAccount: 'Create Account',
    limitTable: 'Limit Table',
    limitTableView: 'Limit Table (View)',
    changePassword: 'Change Password',
    editCreditLimit: 'Edit Credit Limit',
    editFightPct: 'Edit Fight %',
    editTicketPrice: 'Edit Ticket Price',
    activateAccount: 'Activate Account',
    suspendAccount: 'Suspend Account',
    banAccount: 'Ban Account',
    deleteUser: 'Delete User',
    actionsLabel: 'Actions',
    actionsFor: 'Actions for',
    newPassword: 'New Password',
    newCreditLimit: 'New Credit Limit (RM)',
    newFightPct: 'New Fight % (0–100)',
    newTicketPrice: 'New Ticket Price (RM)',
    saveChanges: 'Save Changes',
    cancel: 'Cancel',
    deleteConfirm: 'Are you sure you want to permanently delete',
    deleteWarning: 'Warning: This will also delete subordinate accounts!',
    deleteSubWarning: 'All agents and players under this user will be permanently removed.',
    deleteUndo: 'This action cannot be undone. All data will be permanently lost.',
    limitTableTitle: 'Limit Table',
    backToMatches: 'Back to Matches',
    close: 'Close',
    viewOnly: 'View Only',
    setToAllScores: 'Set To All Scores',
    setToAllDesc: 'Enter a value and press Apply to fill all scores at once (capped by your allocated limits).',
    setToAllScoresDesc: 'Enter a value and press Apply to fill all scores at once (capped by your allocated limits).',
    apply: 'Apply',
    colScore: 'Score',
    colOdds: 'Odds',
    colBetsPlaced: 'Bets Placed',
    colLimit: 'Limit',
    colRemaining: 'Remaining',
    score: 'Score',
    odds: 'Odds',
    betsPlaced: 'Bets Placed',
    limit: 'Limit',
    remaining: 'Remaining',
    loading: 'Loading...',
    selectMatch: 'Select a match',
    viewLimitsFor: 'View the per-score bet limits for',
    selectMatchForLimits: 'Select a match to set per-score bet limits for',
    andDownline: 'and their downline',
    noMatches: 'No matches available.',
    currentLimitsFor: 'These are the current bet limits for',
    contactUpline: 'Contact your upline to change limits.',
    setMaxBetDesc: 'Set a maximum total bet amount per score. Leave blank or 0 for no limit. Once the total bets placed by all users under',
    setMaxBetDescEnd: 'reach the limit, no further bets can be placed for that score.',
    logInTime: 'Log In Date & Time',
    logOutTime: 'Log Out Date & Time',
    ipAddress: 'IP Address',
    duration: 'Duration',
    homeWin: '🏠 Home Win',
    awayWin: '✈️ Away Win',
    draw: '🤝 Draw',
    aos: '🎲 Any Other Score (AOS)',
    saveLimits: 'Save Limits',
    saveAllLimits: 'Save All Limits',
    saving: 'Saving…',
    userLog: 'User Log',
    userLogSubtitle: 'Login history for all users under your account.',
    filterByUsername: 'Filter by username...',
    from: 'From',
    to: 'To',
    search: 'Search',
    clear: 'Clear',
    colLogIn: 'Log In Date & Time',
    colLogOut: 'Log Out Date & Time',
    colIpAddress: 'IP Address',
    colDuration: 'Duration',
    noLogs: 'No login records found.',
    loadingLogs: 'Loading logs...',
    active: 'Active',
    companyFixedFight: 'Company fight % is fixed at 100%',
    viewReadOnly: 'These are the current bet limits. Contact your upline to change limits.',
    editLimitsNote: 'Set a maximum total bet amount per score. Leave blank or 0 for no limit.',
    view: 'View',
    adjustBalance: 'Adjust Balance',
    deleteWarningDetail: 'All agents and players under this user will be permanently removed.',
    deleteIrreversible: 'This action cannot be undone. All data will be permanently lost.',
    updateFor: 'Update settings for',
    newBalance: 'New Balance (RM)',
    enterPassword: 'Enter new password',
    setCreditLimitNote: 'Enter the new credit limit. Cannot exceed your available credit.',
    passwordUpdated: 'Password changed successfully',
    creditUpdated: 'Credit limit updated successfully',
    statusUpdated: 'Account status updated',
    userCreated: 'User created successfully',
    userDeleted: 'User deleted successfully',
    updateFailed: 'Failed to update user',
    createFailed: 'Failed to create user',
    deleteFailed: 'Failed to delete user',
    statusFailed: 'Failed to update user status',
    // Nickname / Commission / Fight labels
    nickname: 'Nickname',
    nicknameOptional: 'Nickname (optional — shown on receipts)',
    nicknameReceipt: 'Display name on receipts',
    setNickname: 'Set Nickname',
    setNicknamePrompt: 'Please set your nickname by pressing here.',
    viewFightHistory: 'View Fight % History',
    editCommission: 'Edit Commission',
    fightLimit: 'Fight % Limit',
    colNickname: 'Nickname',
    colCommission: 'Rebate',
    colFight: 'Fight',
    colFightOnOff: 'Fight On/Off',
    commission: 'Commission',
    fight: 'Fight',
    fightOnOff: 'Fight On/Off',
    commissionPct: 'Commission (%)',
    fightPctLimitLabel: 'Fight % Limit',
  },
};

const zh: Translations = {
  login: {
    welcomeBack: '欢迎回来',
    enterCredentials: '请输入您的凭证以访问仪表板',
    username: '用户名',
    usernamePlaceholder: '请输入用户名',
    password: '密码',
    signIn: '登录',
    authenticating: '验证中...',
    error: '错误',
    secureSSL: '安全 SSL',
    encryption: '256位加密',
    copyright: '© 2026 版权所有。',
    captchaLabel: '安全验证码',
    captchaPlaceholder: '请输入上方验证码',
    captchaError: '验证码错误，请重试。',
    captchaRefresh: '刷新',
    accountLocked: '账户已锁定',
    attemptsRemaining: '次尝试后将锁定账户',
  },
  nav: {
    matchBetting: '投注',
    betHistory: '投注记录',
    afterMatchResults: '报告',
    userSystem: '用户系统',
    matchManagement: '赛事管理',
    preMatchSummary: '焦点预测',
    focusPrediction2: '焦点预测 2',
    signOut: '退出登录',
    balance: '余额',
    credit: '信用额度',
    status: '状态',
    reportsOnly: '仅限报告模式',
  },
  status: {
    active: '正常',
    suspended: '已暂停',
    banned: '已封禁',
  },
  tier: {
    master: '主账号',
    company: '公司',
    agent: '代理',
    player: '玩家',
  },
  reports: {
    pageTitle: '投注记录',
    pageSubtitle: '查看您及下线的所有投注记录。',
    filters: '筛选',
    dateFrom: '开始日期',
    dateTo: '结束日期',
    match: '比赛',
    allMatches: '全部比赛',
    allDates: '全部日期',
    username: '用户名',
    dateTime: '日期与时间',
    score: '比分',
    odd: '赔率',
    betPlaced: '投注额',
    ticketRate: '票价',
    statusCol: '状态',
    totalBet: '总投注',
    grandTotal: '总计',
    noBets: '未找到符合筛选条件的投注记录。',
    pending: '已接受',
    closed: '已结束',
    cancelled: '已取消',
    cancelBet: '取消投注',
    cancelling: '取消中...',
    copyright: '© 2026 ProjectViva。所有计算均按RM1.00基础票价和各级别抽水比例进行。',
    transactionId: '交易编号',
    matchDateRange: '比赛日期范围',
    from: '开始',
    to: '结束',
    showingDates: '显示比赛日期：',
    void: '撤销',
    reBet: '重新投注',
    receipt: '收据',
    confirmVoid: '确认撤销投注',
    confirmVoidDesc: '请输入密码以确认取消此投注。',
    confirmVoidBtn: '确认撤销',
    voiding: '撤销中…',
    incorrectPassword: '密码错误，撤销已取消。',
    enterPasswordPlaceholder: '密码',
    shareImage: '分享图片',
    saveImage: '保存图片',
    printReceipt: '打印收据',
    textShare: '纯文字分享（WhatsApp / 微信 / Telegram）',
    closeReceipt: '✕ 关闭收据',
    preparingImage: '正在准备图片…',
    sharing: '分享中…',
    soldOutNotice: '已售完提示',
    soldOutLimit: '已售完 — 已达限额',
    soldOut: '已售完',
    cancel: '取消',
    totalBetLabel: '总投注额',
    tierAgent: '代理',
    tierPlayer: '玩家',
    tierCompany: '公司',
  },
  wallet: {
    pageTitle: '报告',
    pageSubtitle: '查看您所在层级赛后的输赢总额及余额。',
    secureTransactions: '安全交易',
    footerNote: '* 入款和出款操作仅限公司级别管理员。所有投注余额均按公式计算：输赢 × 抽水% × 票价。',
    copyright: '© 2026 足球投注演示平台。版权所有。',
    filters: '筛选',
    date: '日期',
    match: '比赛',
    allMatches: '全部比赛',
    allDates: '全部日期',
    dateFrom: '开始日期',
    dateTo: '结束日期',
    username: '用户名',
    dateTime: '日期与时间',
    matchCol: '比赛',
    score: '比分',
    odd: '赔率',
    betPlaced: '投注额',
    ticketRate: '票价',
    totalBet: '总投注',
    winLose: '输/赢',
    balanceBeforeFight: '抽水前余额',
    fightPct: '抽水%',
    balance: '余额',
    won: '赢',
    lost: '输',
    grandTotal: '总计',
    noBets: '未找到已结算的投注记录。',
    showDetails: '显示详情',
    commission: '佣金',
    fight: '吃成',
    total: '合计',
    downline: '下线',
    company: '公司',
    betPlacedCol: '投注额',
    winLoseCol: '输赢',
  },
  users: {
    pageTitle: '用户系统',
    pageSubtitle: '管理您的网络层级，配置账户权限，监控代理业绩。',
    adminControl: '管理员控制',
    cascadingStatus: '级联状态',
    cascadingStatusDesc: '暂停或封禁上级账户（公司或代理）将自动限制该线路下所有下级代理和玩家的访问权限。',
    ticketPricing: '票价设置',
    ticketPricingDesc: '公司设置代理价格（RM 0.01 - RM 1.00）。代理设置下级价格 ≥ 自身价格。级联定价系统确保层级正确。',
    fightPercentage: '抽水比例上限',
    fightPercentageDesc: '抽水比例上限决定各级别可分配的最高抽水比例。公司设定基准，代理管理各自线路的上限。',
  },
  matches: {
    pageTitle: '投注',
    selectMatch: '请选择一场比赛进行投注。只有已设置赔率的比赛才可投注。',
    availableMatches: '可用比赛',
    noUpcoming: '暂无即将进行的比赛',
    noUpcomingDesc: '请稍后再查看新赛程。',
    homeWin: '主队赢',
    draw: '平局',
    awayWin: '客队赢',
    equalScore: '平分',
    bettingClosed: '投注已关闭',
    closesIn: '关闭于',
    startsIn: '开始于',
    bettingOptions: '个可投注选项',
    placeBets: '下注',
    placing: '提交中...',
    backToMatches: '返回比赛列表',
    stake: '投注额：',
    win: '赢：',
    noBettingOdds: '暂无投注赔率',
    contactAdmin: '请联系管理员设置赔率',
    editMatch: '编辑比赛详情',
    editMatchDesc: '调整队名、比赛时间和赔率。',
    homeTeam: '主队',
    awayTeam: '客队',
    startTime: '开始时间',
    cancel: '取消',
    saveChanges: '保存更改',
    enterBetAmount: '请至少输入一个投注金额',
    closed: '已关闭',
    viewOdds: '查看赔率',
    shareMatchOdds: '分享比赛赔率',
    loadingFlags: '加载旗帜并生成中…',
    openingShare: '打开分享…',
    shareImage: '分享图片',
    saveImageTitle: '保存图片',
    saveOpenInstructions: '请保存图片后，在 WhatsApp / Telegram / 微信 中分享。',
    soldOut: '已售完',
    remark: '备注',
    remarkPlaceholder: '可选（最多15字）',
    viewOnly: '仅供查看 — 赔率仅作参考，只有玩家账户可以投注。',
    odds: '赔率',
  },
  preMatch: {
    pageTitle: '焦点预测',
    pageSubtitle: '查看您下线各比分的总票数（计入票价后）。',
    selectMatch: '请选择一场比赛以查看焦点预测。',
    noMatches: '暂无即将进行的比赛。',
    score: '比分',
    odds: '赔率',
    totalAfterRate: '总投注额',
    rebate: '佣金',
    focus: '焦点',
    possibleWinLoss: '可能盈亏',
    totalAfterCommission: '佣金后总计',
    backToMatches: '返回比赛列表',
    noDownline: '本场比赛暂无下线投注记录。',
    username: '用户名',
    ticketRate: '票价',
    tickets: '票数',
  },
  dashboard: {
    loading: '加载中...',
  },
  common: {
    loading: '加载中...',
    allRightsReserved: '© 2026 BETPRO 平台 | 版权所有',
  },
  userMgmt: {
    title: '用户系统',
    subtitle: '管理账户、层级结构和权限。',
    createUser: '创建用户',
    searchPlaceholder: '搜索用户名...',
    expandAll: '全部展开',
    collapseAll: '全部收起',
    loadingUsers: '加载用户中...',
    errorLoading: '加载用户失败',
    noUsers: '未找到用户。',
    colUsername: '用户名',
    colTier: '级别',
    colStatus: '状态',
    colCreditLimit: '信用额度',
    colCreditAlloc: '信用分配',
    colFightPct: '抽水%',
    colTicketPrice: '票价',
    colActions: '操作',
    availableCredit: '可用信用额度',
    allocated: '已分配',
    available: '可用',
    createNewUser: '创建新用户账户',
    createDesc: '向系统添加新用户。',
    initialPassword: '初始密码',
    accountTier: '账户级别',
    selectTier: '选择级别',
    ticketPrice: '票价 (RM)',
    fightPct: '抽水%',
    selectFightPct: '选择抽水%',
    creditLimit: '信用额度 (RM)',
    createAccount: '创建账户',
    limitTable: '限额表',
    limitTableView: '限额表（只读）',
    changePassword: '修改密码',
    editCreditLimit: '编辑信用额度',
    editFightPct: '编辑抽水%',
    editTicketPrice: '编辑票价',
    activateAccount: '激活账户',
    suspendAccount: '暂停账户',
    banAccount: '封禁账户',
    deleteUser: '删除用户',
    actionsLabel: '操作',
    actionsFor: '操作',
    newPassword: '新密码',
    newCreditLimit: '新信用额度 (RM)',
    newFightPct: '新抽水% (0–100)',
    newTicketPrice: '新票价 (RM)',
    saveChanges: '保存更改',
    cancel: '取消',
    deleteConfirm: '确定要永久删除',
    deleteWarning: '警告：此操作同时删除下级账户！',
    deleteSubWarning: '该用户下的所有代理和玩家将被永久删除。',
    deleteUndo: '此操作不可撤销，所有数据将永久丢失。',
    limitTableTitle: '限额表',
    backToMatches: '返回比赛列表',
    close: '关闭',
    viewOnly: '仅查看',
    setToAllScores: '设置所有比分',
    setToAllDesc: '输入数值后点击应用，将一次性填入所有比分（受上级限额约束）。',
    setToAllScoresDesc: '输入数值后点击应用，将一次性填入所有比分（受上级限额约束）。',
    apply: '应用',
    colScore: '比分',
    colOdds: '赔率',
    colBetsPlaced: '已投注',
    colLimit: '限额',
    colRemaining: '剩余',
    score: '比分',
    odds: '赔率',
    betsPlaced: '已投注',
    limit: '限额',
    remaining: '剩余',
    loading: '加载中...',
    selectMatch: '选择比赛',
    viewLimitsFor: '查看以下用户的各比分投注限额：',
    selectMatchForLimits: '请选择一场比赛，为以下用户设置各比分投注限额：',
    andDownline: '及其下线',
    noMatches: '暂无可用比赛。',
    currentLimitsFor: '以下是当前投注限额（用户：',
    contactUpline: '如需更改限额，请联系上级。',
    setMaxBetDesc: '为每个比分设置最大总投注额，留空或0表示无限制。以下用户的下线所有投注超过限额后将无法继续投注：',
    setMaxBetDescEnd: '的下线达到限额后将无法继续投注该比分。',
    logInTime: '登录日期与时间',
    logOutTime: '登出日期与时间',
    ipAddress: 'IP 地址',
    duration: '时长',
    homeWin: '🏠 主队赢',
    awayWin: '✈️ 客队赢',
    draw: '🤝 平局',
    aos: '🎲 其他比分 (AOS)',
    saveLimits: '保存限额',
    saveAllLimits: '保存所有限额',
    saving: '保存中…',
    userLog: '用户日志',
    userLogSubtitle: '您账户下所有用户的登录记录。',
    filterByUsername: '按用户名筛选...',
    from: '开始日期',
    to: '结束日期',
    search: '搜索',
    clear: '清除',
    colLogIn: '登录日期与时间',
    colLogOut: '登出日期与时间',
    colIpAddress: 'IP 地址',
    colDuration: '时长',
    noLogs: '未找到登录记录。',
    loadingLogs: '加载日志中...',
    active: '活跃',
    companyFixedFight: '公司抽水%固定为100%',
    viewReadOnly: '以下为当前投注限额，如需更改请联系上级。',
    editLimitsNote: '为每个比分设置最大总投注额，留空或0表示无限制。',
    view: '查看',
    adjustBalance: '调整余额',
    deleteWarningDetail: '该用户下的所有代理和玩家将被永久删除。',
    deleteIrreversible: '此操作不可撤销，所有数据将永久丢失。',
    updateFor: '更新设置对象',
    newBalance: '新余额（RM）',
    enterPassword: '输入新密码',
    setCreditLimitNote: '输入新的信用额度，不能超过您的可用信用。',
    passwordUpdated: '密码已成功修改',
    creditUpdated: '信用额度已成功更新',
    statusUpdated: '账户状态已更新',
    userCreated: '用户创建成功',
    userDeleted: '用户删除成功',
    updateFailed: '更新用户失败',
    createFailed: '创建用户失败',
    deleteFailed: '删除用户失败',
    statusFailed: '更新用户状态失败',
    // Nickname / Commission / Fight labels
    nickname: '昵称',
    nicknameOptional: '昵称（选填 — 显示于收据）',
    nicknameReceipt: '收据上的显示名称',
    setNickname: '设置昵称',
    setNicknamePrompt: '请点击这里设置您的昵称。',
    viewFightHistory: '查看吃成历史',
    editCommission: '编辑佣金',
    fightLimit: '吃成上限',
    colNickname: '昵称',
    colCommission: '佣金',
    colFight: '吃成',
    colFightOnOff: '吃成开关',
    commission: '佣金',
    fight: '吃成',
    fightOnOff: '吃成开关',
    commissionPct: '佣金 (%)',
    fightPctLimitLabel: '吃成上限',
  },
};

const bm: Translations = {
  login: {
    welcomeBack: 'Selamat Kembali',
    enterCredentials: 'Masukkan kelayakan anda untuk mengakses papan pemuka',
    username: 'Nama Pengguna',
    usernamePlaceholder: 'Masukkan nama pengguna anda',
    password: 'Kata Laluan',
    signIn: 'Log Masuk',
    authenticating: 'Mengesahkan...',
    error: 'Ralat',
    secureSSL: 'SSL Selamat',
    encryption: 'Enkripsi 256-bit',
    copyright: '© 2026 Hak Cipta Terpelihara.',
    captchaLabel: 'Kod Keselamatan',
    captchaPlaceholder: 'Masukkan kod di atas',
    captchaError: 'Kod keselamatan salah. Sila cuba lagi.',
    captchaRefresh: 'Muat Semula',
    accountLocked: 'Akaun Dikunci',
    attemptsRemaining: 'percubaan tersisa sebelum dikunci',
  },
  nav: {
    matchBetting: 'Pertaruhan Perlawanan',
    betHistory: 'Sejarah Pertaruhan',
    afterMatchResults: 'Keputusan Selepas Perlawanan',
    userSystem: 'Sistem Pengguna',
    matchManagement: 'Pengurusan Perlawanan',
    preMatchSummary: 'Ramalan Fokus',
    focusPrediction2: 'Ramalan Fokus 2',
    signOut: 'Log Keluar',
    balance: 'Baki',
    credit: 'Kredit',
    status: 'Status',
    reportsOnly: 'Mod Laporan Sahaja',
  },
  status: {
    active: 'AKTIF',
    suspended: 'DIGANTUNG',
    banned: 'DIHARAMKAN',
  },
  tier: {
    master: 'MASTER',
    company: 'SYARIKAT',
    agent: 'EJEN',
    player: 'PEMAIN',
  },
  reports: {
    pageTitle: 'Sejarah Pertaruhan',
    pageSubtitle: 'Semak semua pertaruhan yang dibuat oleh anda dan orang bawahan anda.',
    filters: 'Penapis',
    dateFrom: 'Dari',
    dateTo: 'Hingga',
    match: 'Perlawanan',
    allMatches: 'Semua Perlawanan',
    allDates: 'Semua Tarikh',
    username: 'Nama Pengguna',
    dateTime: 'Tarikh & Masa',
    score: 'Skor',
    odd: 'Odd',
    betPlaced: 'Pertaruhan',
    ticketRate: 'Kadar Tiket',
    statusCol: 'Status',
    totalBet: 'Jumlah Pertaruhan',
    grandTotal: 'JUMLAH BESAR',
    noBets: 'Tiada pertaruhan ditemui untuk penapis yang dipilih.',
    pending: 'Diterima',
    closed: 'Ditutup',
    cancelled: 'Dibatalkan',
    cancelBet: 'Batal Pertaruhan',
    cancelling: 'Membatalkan...',
    copyright: '© 2026 ProjectViva. Semua pengiraan mengikut harga tiket asas RM1.00 dan peratusan lawan mengikut peringkat.',
    transactionId: 'ID Transaksi',
    matchDateRange: 'Julat Tarikh Perlawanan',
    from: 'Dari',
    to: 'Hingga',
    showingDates: 'Menunjukkan tarikh perlawanan:',
    void: 'Batal',
    reBet: 'Pertaruhan Semula',
    receipt: 'Resit',
    confirmVoid: 'Sahkan Pembatalan Pertaruhan',
    confirmVoidDesc: 'Masukkan kata laluan anda untuk mengesahkan pembatalan pertaruhan ini.',
    confirmVoidBtn: 'Sahkan Pembatalan',
    voiding: 'Membatalkan…',
    incorrectPassword: 'Kata laluan salah. Pembatalan dibatalkan.',
    enterPasswordPlaceholder: 'Kata Laluan',
    shareImage: 'Kongsi Imej',
    saveImage: 'Simpan Imej',
    printReceipt: 'Cetak Resit',
    textShare: 'Kongsi Teks Sahaja (WhatsApp / WeChat / Telegram)',
    closeReceipt: '✕ Tutup Resit',
    preparingImage: 'Menyediakan imej…',
    sharing: 'Berkongsi…',
    soldOutNotice: 'Notis Habis Dijual',
    soldOutLimit: 'Habis Dijual — Had Dicapai',
    soldOut: 'habis dijual',
    cancel: 'Batal',
    totalBetLabel: 'Jumlah Pertaruhan',
    tierAgent: 'Ejen',
    tierPlayer: 'Pemain',
    tierCompany: 'Syarikat',
  },
  wallet: {
    pageTitle: 'Keputusan Selepas Perlawanan',
    pageSubtitle: 'Lihat jumlah menang/kalah dan baki selepas penyelesaian perlawanan untuk hierarki anda.',
    secureTransactions: 'Transaksi Selamat',
    footerNote: '* Operasi masuk dan keluar wang terhad kepada pentadbir Peringkat Syarikat. Semua baki pertaruhan dikira menggunakan formula: Menang/Kalah × % Lawan × Harga Tiket.',
    copyright: '© 2026 Platform Demo Pertaruhan Bola Sepak. Hak cipta terpelihara.',
    filters: 'Penapis',
    date: 'Tarikh',
    match: 'Perlawanan',
    allMatches: 'Semua Perlawanan',
    allDates: 'Semua Tarikh',
    dateFrom: 'Dari',
    dateTo: 'Hingga',
    username: 'Nama Pengguna',
    dateTime: 'Tarikh & Masa',
    matchCol: 'Perlawanan',
    score: 'Skor',
    odd: 'Odd',
    betPlaced: 'Pertaruhan',
    ticketRate: 'Kadar Tiket',
    totalBet: 'Jumlah Pertaruhan',
    winLose: 'Menang / Kalah',
    balanceBeforeFight: 'Baki Sebelum Lawan',
    fightPct: '% Lawan',
    balance: 'Baki',
    won: 'MENANG',
    lost: 'KALAH',
    grandTotal: 'JUMLAH BESAR',
    noBets: 'Tiada pertaruhan yang diselesaikan ditemui.',
    showDetails: 'Tunjuk Butiran',
    commission: 'Rebat',
    fight: 'Lawan',
    total: 'Jumlah',
    downline: 'Bawahan',
    company: 'Syarikat',
    betPlacedCol: 'Pertaruhan',
    winLoseCol: 'Menang/Kalah',
  },
  users: {
    pageTitle: 'Sistem Pengguna',
    pageSubtitle: 'Urus hierarki rangkaian anda, konfigurasikan kebenaran akaun, dan pantau prestasi ejen.',
    adminControl: 'Kawalan Pentadbiran',
    cascadingStatus: 'Status Berturutan',
    cascadingStatusDesc: 'Menggantung atau mengharamkan akaun induk (Syarikat atau Ejen) akan secara automatik menyekat akses untuk semua ejen dan pemain hiliran dalam talian tersebut.',
    ticketPricing: 'Harga Tiket',
    ticketPricingDesc: 'Syarikat menetapkan harga ejen (RM 0.01 - RM 1.00). Ejen menetapkan harga orang bawahan ≥ harga mereka sendiri. Sistem harga berturutan memastikan hierarki yang betul.',
    fightPercentage: 'Peratusan Lawan',
    fightPercentageDesc: 'Peratusan lawan menentukan perkongsian risiko antara peringkat. Syarikat menetapkan garis asas, dan Ejen mengurus peratusan untuk talian masing-masing.',
  },
  matches: {
    pageTitle: 'Pertaruhan Perlawanan',
    selectMatch: 'Pilih perlawanan untuk membuat pertaruhan. Hanya perlawanan dengan odd yang ditetapkan tersedia.',
    availableMatches: 'Perlawanan Tersedia',
    noUpcoming: 'Tiada Perlawanan Akan Datang',
    noUpcomingDesc: 'Semak semula kemudian untuk perlawanan baharu.',
    homeWin: 'TUAN RUMAH MENANG',
    draw: 'SERI',
    awayWin: 'PELAWAT MENANG',
    equalScore: 'Skor Sama',
    bettingClosed: 'Pertaruhan Ditutup',
    closesIn: 'Ditutup dalam',
    startsIn: 'Bermula dalam',
    bettingOptions: 'pilihan pertaruhan tersedia',
    placeBets: 'Buat Pertaruhan',
    placing: 'Memproses...',
    backToMatches: 'Kembali ke Perlawanan',
    stake: 'Pertaruhan:',
    win: 'Menang:',
    noBettingOdds: 'Tiada odd pertaruhan tersedia',
    contactAdmin: 'Hubungi admin untuk menetapkan odd',
    editMatch: 'Edit Butiran Perlawanan',
    editMatchDesc: 'Laraskan nama pasukan, masa perlawanan dan odd pertaruhan.',
    homeTeam: 'Pasukan Tuan Rumah',
    awayTeam: 'Pasukan Pelawat',
    startTime: 'Masa Mula',
    cancel: 'Batal',
    saveChanges: 'Simpan Perubahan',
    enterBetAmount: 'Sila masukkan sekurang-kurangnya satu jumlah pertaruhan',
    closed: 'Ditutup',
    viewOdds: 'Lihat Odd',
    shareMatchOdds: 'Kongsi Odd Perlawanan',
    loadingFlags: 'Memuatkan bendera & menjana…',
    openingShare: 'Membuka perkongsian…',
    shareImage: 'Kongsi Imej',
    saveImageTitle: 'Simpan Imej',
    saveOpenInstructions: 'Simpan imej, kemudian buka WhatsApp / Telegram / WeChat untuk berkongsi.',
    soldOut: 'Habis Dijual',
    remark: 'Catatan',
    remarkPlaceholder: 'Pilihan (maks 15 aksara)',
    viewOnly: 'Lihat sahaja — odd dipaparkan untuk rujukan. Hanya akaun pemain boleh membuat pertaruhan.',
    odds: 'Odd',
  },
  preMatch: {
    pageTitle: 'Ramalan Fokus',
    pageSubtitle: 'Lihat jumlah tiket selepas kadar untuk setiap skor merentas downline anda.',
    selectMatch: 'Pilih perlawanan untuk melihat tetapan pertarungan.',
    noMatches: 'Tiada perlawanan akan datang tersedia.',
    score: 'Skor',
    odds: 'Odd',
    totalAfterRate: 'Jumlah Pertaruhan',
    rebate: 'Rebat',
    focus: 'Fokus',
    possibleWinLoss: 'Kemungkinan Menang/Kalah',
    totalAfterCommission: 'Jumlah Selepas Komisen',
    backToMatches: 'Kembali ke Senarai Perlawanan',
    noDownline: 'Tiada pertaruhan downline ditemui untuk perlawanan ini.',
    username: 'Nama Pengguna',
    ticketRate: 'Kadar Tiket',
    tickets: 'Tiket',
  },
  dashboard: {
    loading: 'Memuatkan...',
  },
  common: {
    loading: 'Memuatkan...',
    allRightsReserved: '© 2026 PLATFORM BETPRO | HAK CIPTA TERPELIHARA',
  },
  userMgmt: {
    title: 'Sistem Pengguna',
    subtitle: 'Urus akaun, hierarki, dan kebenaran.',
    createUser: 'Cipta Pengguna',
    searchPlaceholder: 'Cari nama pengguna...',
    expandAll: 'Kembangkan Semua',
    collapseAll: 'Runtuhkan Semua',
    loadingUsers: 'Memuatkan pengguna...',
    errorLoading: 'Ralat memuatkan pengguna',
    noUsers: 'Tiada pengguna ditemui.',
    colUsername: 'Nama Pengguna',
    colTier: 'Peringkat',
    colStatus: 'Status',
    colCreditLimit: 'Had Kredit',
    colCreditAlloc: 'Peruntukan Kredit',
    colFightPct: '% Lawan',
    colTicketPrice: 'Harga Tiket',
    colActions: 'Tindakan',
    availableCredit: 'Kredit Tersedia',
    allocated: 'Diperuntukkan',
    available: 'Tersedia',
    createNewUser: 'Cipta Akaun Pengguna Baharu',
    createDesc: 'Tambah pengguna baharu ke sistem.',
    initialPassword: 'Kata Laluan Awal',
    accountTier: 'Peringkat Akaun',
    selectTier: 'Pilih peringkat',
    ticketPrice: 'Harga Tiket (RM)',
    fightPct: '% Lawan',
    selectFightPct: 'Pilih % lawan',
    creditLimit: 'Had Kredit (RM)',
    createAccount: 'Cipta Akaun',
    limitTable: 'Jadual Had',
    limitTableView: 'Jadual Had (Lihat)',
    changePassword: 'Tukar Kata Laluan',
    editCreditLimit: 'Edit Had Kredit',
    editFightPct: 'Edit % Lawan',
    editTicketPrice: 'Edit Harga Tiket',
    activateAccount: 'Aktifkan Akaun',
    suspendAccount: 'Gantung Akaun',
    banAccount: 'Haramkan Akaun',
    deleteUser: 'Padam Pengguna',
    actionsLabel: 'Tindakan',
    actionsFor: 'Tindakan untuk',
    newPassword: 'Kata Laluan Baharu',
    newCreditLimit: 'Had Kredit Baharu (RM)',
    newFightPct: '% Lawan Baharu (0–100)',
    newTicketPrice: 'Harga Tiket Baharu (RM)',
    saveChanges: 'Simpan Perubahan',
    cancel: 'Batal',
    deleteConfirm: 'Adakah anda pasti mahu memadam secara kekal',
    deleteWarning: 'Amaran: Ini juga akan memadam akaun bawahan!',
    deleteSubWarning: 'Semua ejen dan pemain di bawah pengguna ini akan dipadam secara kekal.',
    deleteUndo: 'Tindakan ini tidak boleh dibatalkan. Semua data akan hilang secara kekal.',
    limitTableTitle: 'Jadual Had',
    backToMatches: 'Kembali ke Perlawanan',
    close: 'Tutup',
    viewOnly: 'Lihat Sahaja',
    setToAllScores: 'Tetapkan Ke Semua Skor',
    setToAllDesc: 'Masukkan nilai dan tekan Guna untuk isi semua skor sekaligus.',
    setToAllScoresDesc: 'Masukkan nilai dan tekan Guna untuk isi semua skor sekaligus (dihadkan oleh had yang diperuntukkan).',
    apply: 'Guna',
    colScore: 'Skor',
    colOdds: 'Odd',
    colBetsPlaced: 'Pertaruhan Dibuat',
    colLimit: 'Had',
    colRemaining: 'Baki',
    score: 'Skor',
    odds: 'Odd',
    betsPlaced: 'Pertaruhan Dibuat',
    limit: 'Had',
    remaining: 'Baki',
    loading: 'Memuatkan...',
    selectMatch: 'Pilih perlawanan',
    viewLimitsFor: 'Lihat had pertaruhan per skor untuk',
    selectMatchForLimits: 'Pilih perlawanan untuk menetapkan had pertaruhan per skor untuk',
    andDownline: 'dan downline mereka',
    noMatches: 'Tiada perlawanan tersedia.',
    currentLimitsFor: 'Ini adalah had pertaruhan semasa untuk',
    contactUpline: 'Hubungi upline anda untuk menukar had.',
    setMaxBetDesc: 'Tetapkan jumlah pertaruhan maksimum per skor. Kosongkan atau 0 untuk tiada had. Setelah had dicapai oleh downline',
    setMaxBetDescEnd: 'tiada lagi pertaruhan boleh dibuat untuk skor tersebut.',
    logInTime: 'Tarikh & Masa Log Masuk',
    logOutTime: 'Tarikh & Masa Log Keluar',
    ipAddress: 'Alamat IP',
    duration: 'Tempoh',
    homeWin: '🏠 Tuan Rumah Menang',
    awayWin: '✈️ Pelawat Menang',
    draw: '🤝 Seri',
    aos: '🎲 Skor Lain (AOS)',
    saveLimits: 'Simpan Had',
    saveAllLimits: 'Simpan Semua Had',
    saving: 'Menyimpan…',
    userLog: 'Log Pengguna',
    userLogSubtitle: 'Sejarah log masuk untuk semua pengguna di bawah akaun anda.',
    filterByUsername: 'Tapis mengikut nama pengguna...',
    from: 'Dari',
    to: 'Hingga',
    search: 'Cari',
    clear: 'Kosongkan',
    colLogIn: 'Tarikh & Masa Log Masuk',
    colLogOut: 'Tarikh & Masa Log Keluar',
    colIpAddress: 'Alamat IP',
    colDuration: 'Tempoh',
    noLogs: 'Tiada rekod log masuk ditemui.',
    loadingLogs: 'Memuatkan log...',
    active: 'Aktif',
    companyFixedFight: '% lawan syarikat ditetapkan pada 100%',
    viewReadOnly: 'Ini adalah had pertaruhan semasa. Hubungi upline anda untuk menukarnya.',
    editLimitsNote: 'Tetapkan jumlah pertaruhan maksimum per skor. Kosongkan atau 0 untuk tiada had.',
    view: 'Lihat',
    adjustBalance: 'Laraskan Baki',
    deleteWarningDetail: 'Semua ejen dan pemain di bawah pengguna ini akan dipadam secara kekal.',
    deleteIrreversible: 'Tindakan ini tidak boleh dibatalkan. Semua data akan hilang secara kekal.',
    updateFor: 'Kemas kini tetapan untuk',
    newBalance: 'Baki Baharu (RM)',
    enterPassword: 'Masukkan kata laluan baharu',
    setCreditLimitNote: 'Masukkan had kredit baharu. Tidak boleh melebihi kredit tersedia anda.',
    passwordUpdated: 'Kata laluan berjaya ditukar',
    creditUpdated: 'Had kredit berjaya dikemas kini',
    statusUpdated: 'Status akaun dikemas kini',
    userCreated: 'Pengguna berjaya dicipta',
    userDeleted: 'Pengguna berjaya dipadam',
    updateFailed: 'Gagal mengemas kini pengguna',
    createFailed: 'Gagal mencipta pengguna',
    deleteFailed: 'Gagal memadam pengguna',
    statusFailed: 'Gagal mengemas kini status pengguna',
    // Nickname / Commission / Fight labels
    nickname: 'Nama Paparan',
    nicknameOptional: 'Nama Paparan (pilihan — ditunjukkan pada resit)',
    nicknameReceipt: 'Nama paparan pada resit',
    setNickname: 'Tetapkan Nama Paparan',
    setNicknamePrompt: 'Sila tetapkan nama paparan anda dengan menekan di sini.',
    viewFightHistory: 'Lihat Sejarah % Lawan',
    editCommission: 'Edit Komisen',
    fightLimit: 'Had % Lawan',
    colNickname: 'Nama Paparan',
    colCommission: 'Rebat',
    colFight: 'Lawan',
    colFightOnOff: 'Lawan Hidup/Mati',
    commission: 'Komisen',
    fight: 'Lawan',
    fightOnOff: 'Lawan Hidup/Mati',
    commissionPct: 'Komisen (%)',
    fightPctLimitLabel: 'Had % Lawan',
  },
};

export const translations: Record<Language, Translations> = { en, zh, bm };

// ─── Context ───────────────────────────────────────────────────────────────────
interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const STORAGE_KEY = 'betpro_language';

const LanguageContext = React.createContext<LanguageContextValue>({
  language: 'en',
  setLanguage: () => {},
  t: en,
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem(STORAGE_KEY) as Language | null;
    return saved && ['en', 'zh', 'bm'].includes(saved) ? saved : 'en';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
  };

  const value: LanguageContextValue = {
    language,
    setLanguage,
    t: translations[language],
  };

  return React.createElement(LanguageContext.Provider, { value }, children);
}

export function useLanguage() {
  return useContext(LanguageContext);
}
